/**
 * Frame-**integrated** gear — the frame-side sibling of `talentIntegratedGear.ts`.
 * A frame can hard-mount equipment that is never bought, licensed or slotted: the
 * Sherman's ZF4 SOLIDCORE (`mw_sherman_integrated`), the Barbarossa's Apocalypse
 * Rail, the Lancaster's Latch Drone, the Raleigh's M35 Mjolnir. lancer-data records
 * the link on the **frame** — `frame.integrated[]` and `frame.core_system.integrated[]`
 * — and the encounter freeze already reads it (`@lancer-vtt/rules`'s `spawn.ts`
 * `frameIntegratedWeaponIds`, frozen into its own `integrated:${i}` key namespace,
 * outside `base.mounts`).
 *
 * The **build** side needs its own notion, because the item itself carries no
 * back-pointer to its frame — no `source`, no `license`, no `talent_id` — so every
 * gate in the equip picker's lock chain (`licenseLock`, `talentLock`,
 * `integratedLock`) returns `null` and ZF4 SOLIDCORE would be offered as a freely
 * addable Main weapon on a GMS Everest. This module keys off the **frame** side
 * exactly as `isIntegratedTalentItem` keys off the talent side.
 *
 * Pure reads over the compendium — no pilot in scope, no storage, no command. An
 * integrated item is never manually equippable *by anyone*, including a pilot who
 * actually flies the granting frame: the frame arms it at spawn, slot- and
 * SP-exempt, so a manual equip would double-grant it and (RAW-wrongly) burn a real
 * mount slot.
 *
 * Matches COMP/CON, which reaches the same conclusion through a coarser heuristic
 * — `MechEquipment.IsIntegrated = talent_item || frame_id || id.includes('_integrated')`
 * plus `LicensedItem`'s `if (!this.Source) this.IsIntegrated = true` — and drops
 * every such item from its weapon selector's pool
 * (`_WeaponSelector.vue`'s `.filter(x => x.Source && …)`). We derive the same set
 * from the data instead of from the id spelling, so a homebrew LCP frame that
 * integrates a normally-spelled item is caught too.
 */

/**
 * The subset of a compendium `frame` entry this module reads: its display `name`
 * (for the picker's lock label) and the two observed integrated-id lists. Both are
 * `unknown` because neither is in `FrameSchema` — they ride through `.passthrough()`
 * untyped, exactly as `spawn.ts` reads them.
 */
export type RawIntegratingFrame = { id: string; name?: string; integrated?: unknown; core_system?: unknown };

/**
 * The read-only compendium slice this module needs: enumerate frames. Structurally
 * typed — mirrors `talentIntegratedGear.ts`'s `TalentIntegratedCompendium` and
 * `sp.ts`'s `SpCompendium` — so `@lancer-vtt/domain` stays free of a hard
 * `@lancer-vtt/compendium` value import; the real `Compendium` class satisfies it.
 */
export type FrameIntegratedCompendium = {
  list(type: "frame"): RawIntegratingFrame[];
};

/** Read a frame's two integrated-id lists as a clean `string[]` (defensive against malformed/missing fields). */
function integratedIdsOf(frame: RawIntegratingFrame): string[] {
  const ids: string[] = [];
  const collect = (v: unknown) => {
    if (Array.isArray(v)) for (const x of v) if (typeof x === "string") ids.push(x);
  };
  collect(frame.integrated);
  const cs = frame.core_system;
  if (cs && typeof cs === "object") collect((cs as Record<string, unknown>).integrated);
  return ids;
}

/**
 * The frame that hard-mounts `itemId`, or `undefined` when no frame does. The
 * picker's lock label names it ("Integrated into the Sherman"), so this returns the
 * frame rather than a bare boolean; {@link isFrameIntegratedItem} is the boolean
 * the reducer wall wants.
 *
 * First match wins — no core frame integrates another frame's item, and if a
 * homebrew pack ever did, naming either one is equally honest.
 */
export function integratingFrame(
  itemId: string,
  compendium: FrameIntegratedCompendium,
): RawIntegratingFrame | undefined {
  for (const frame of compendium.list("frame")) {
    if (integratedIdsOf(frame).includes(itemId)) return frame;
  }
  return undefined;
}

/**
 * The pilot-**independent** predicate: is this item hard-mounted by some frame, and
 * therefore never manually equippable? The reducer wall (`applyEquipWeapon`/
 * `applyEquipSystem`) and the editor picker's `frameIntegratedLock` both consult
 * this, so a greyed picker row is precisely a pick the reducer would bounce.
 */
export function isFrameIntegratedItem(itemId: string, compendium: FrameIntegratedCompendium): boolean {
  return integratingFrame(itemId, compendium) !== undefined;
}
